/**
 * VisuelThemeCard.jsx — Composant visuel générique pour tous les thèmes du Permis AM.
 * Rend une carte pédagogique HTML/CSS alignée sur le style CODE2ROUES,
 * pilotée par une simple config JSON.
 */
import React from 'react';
import { ShieldCheck, AlertTriangle, Lightbulb } from 'lucide-react';

function BrandBadge() {
  return (
    <div className="bg-white rounded-md px-1.5 py-0.5 shadow inline-flex">
      <span className="text-rose-600 font-black text-[10px]">CODE</span>
      <span className="text-slate-900 font-black text-[10px]">2</span>
      <span className="text-rose-600 font-black text-[10px]">ROUES</span>
    </div>
  );
}

export function VisuelThemeCard({ config, variant = 'thumb', color = '#e11d48' }) {
  const isFocus = variant === 'focus';
  const { num, title, subtitle, central, pastilles = [] } = config;

  return (
    <div className={`w-full ${isFocus ? '' : 'h-full aspect-[4/3]'} bg-white text-slate-900 overflow-hidden flex flex-col`} data-testid={`visuel-theme-${num}`}>
      {/* Bandeau haut coloré */}
      <div className="relative px-3 sm:px-6 py-2.5 sm:py-4 flex items-center gap-2 sm:gap-3 shrink-0" style={{ background: `linear-gradient(135deg, ${color}, ${color}cc)` }}>
        <div className={`shrink-0 rounded-full bg-white flex items-center justify-center font-black shadow ${isFocus ? 'w-14 h-14 text-2xl' : 'w-9 h-9 text-base'}`} style={{ color }}>
          {num}
        </div>
        <div className="min-w-0 flex-1 text-white">
          <div className={`font-black uppercase tracking-wider leading-tight ${isFocus ? 'text-2xl sm:text-3xl' : 'text-[13px] sm:text-sm'}`}>{title}</div>
          {subtitle && <div className={`opacity-95 leading-tight mt-0.5 ${isFocus ? 'text-base sm:text-lg' : 'text-[10px] sm:text-xs'}`}>{subtitle}</div>}
        </div>
        <div className={`shrink-0 flex-col items-end ${isFocus ? 'flex' : 'hidden sm:flex'}`}>
          <BrandBadge />
          <div className="mt-0.5 text-[8.5px] font-black tracking-widest text-white/90">PERMIS AM · N°{num}</div>
        </div>
      </div>

      {/* Corps : bloc central + pastilles */}
      <div className={`flex-1 flex flex-col ${isFocus ? 'gap-4 p-4 sm:p-6' : 'gap-2 p-2.5 sm:p-3'}`}>
        {/* Bloc central : idée clé */}
        {central && (
          <div className={`rounded-xl border-2 text-center flex-1 flex flex-col justify-center ${isFocus ? 'p-4' : 'p-2'}`} style={{ borderColor: color, background: `${color}0d` }}>
            {central.big && (
              <div className={`font-black leading-none text-slate-900 ${isFocus ? 'text-4xl sm:text-6xl' : 'text-xl sm:text-2xl'}`} style={{ color }}>
                {central.big}
              </div>
            )}
            {central.title && (
              <div className={`font-black text-slate-900 leading-tight ${isFocus ? 'text-xl sm:text-2xl mt-2' : 'text-[13px] mt-1'}`}>
                {central.title}
              </div>
            )}
            {central.subtitle && (
              <div className={`text-slate-600 leading-tight ${isFocus ? 'text-sm sm:text-base mt-2' : 'text-[10px] mt-0.5'}`}>
                {central.subtitle}
              </div>
            )}
            {central.items && (
              <ul className={`text-slate-700 mt-2 space-y-0.5 text-left ${isFocus ? 'text-sm sm:text-base' : 'text-[10px]'}`}>
                {central.items.map((it, i) => (
                  <li key={i} className="flex gap-1.5"><span style={{ color }}>•</span> {it}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Pastilles pour ta sécurité / à éviter / à retenir (max 3) */}
        {pastilles.length > 0 && (
          <div className={`grid ${pastilles.length === 3 ? 'grid-cols-3' : pastilles.length === 2 ? 'grid-cols-2' : 'grid-cols-1'} ${isFocus ? 'gap-3' : 'gap-1.5'}`}>
            {pastilles.map((p, i) => {
              const tone = p.tone || 'info';
              const styles = {
                success: { bg: 'bg-emerald-50', bd: 'border-emerald-300', tx: 'text-emerald-700', Icon: ShieldCheck, label: p.title || 'Pour ta sécurité' },
                danger:  { bg: 'bg-rose-50',    bd: 'border-rose-300',    tx: 'text-rose-700',    Icon: AlertTriangle, label: p.title || 'À éviter' },
                warning: { bg: 'bg-amber-50',   bd: 'border-amber-300',   tx: 'text-amber-700',   Icon: Lightbulb, label: p.title || 'À retenir' },
                info:    { bg: 'bg-sky-50',     bd: 'border-sky-300',     tx: 'text-sky-700',     Icon: ShieldCheck, label: p.title || 'Info' },
              }[tone];
              const Icon = styles.Icon;
              return (
                <div key={i} className={`rounded-lg border-2 ${styles.bg} ${styles.bd} ${isFocus ? 'p-3' : 'p-1.5'}`}>
                  <div className={`flex items-center gap-1 font-black uppercase tracking-wider ${styles.tx} ${isFocus ? 'text-xs mb-1.5' : 'text-[8px] mb-1'}`}>
                    <Icon className={isFocus ? 'w-4 h-4' : 'w-2.5 h-2.5'} />
                    <span className="truncate">{styles.label}</span>
                  </div>
                  {p.desc && (
                    <div className={`text-slate-800 font-bold leading-tight ${isFocus ? 'text-sm' : 'text-[9.5px]'}`}>
                      {p.desc}
                    </div>
                  )}
                  {p.items && (
                    <ul className={`text-slate-700 leading-tight space-y-0.5 ${isFocus ? 'text-sm space-y-1' : 'text-[9px]'}`}>
                      {p.items.map((it, j) => <li key={j}>· {it}</li>)}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default VisuelThemeCard;
